import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  Users, DollarSign, TrendingUp, Copy, Award, ArrowUpRight, Wallet, RefreshCw
} from 'lucide-react';

const tierColors = {
  bronze: 'bg-amber-700/20 text-amber-500 border-amber-700/30',
  silver: 'bg-slate-400/20 text-slate-300 border-slate-400/30',
  gold: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  platinum: 'bg-cyan-400/20 text-cyan-300 border-cyan-400/30'
};

const IBPortal = () => {
  const [profile, setProfile] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [isPayoutDialogOpen, setIsPayoutDialogOpen] = useState(false);
  
  const [registerData, setRegisterData] = useState({
    company_name: '',
    website: '',
    marketing_methods: ''
  });
  
  const [payoutAmount, setPayoutAmount] = useState(50);

  const fetchData = async () => {
    try {
      const dashboardRes = await api.getIBDashboard();
      setDashboard(dashboardRes.data);
      setProfile(dashboardRes.data.profile);
      
      const referralsRes = await api.getIBReferrals();
      setReferrals(referralsRes.data);
    } catch (error) {
      if (error.response?.status === 404) {
        // Not registered as IB
        setProfile(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      await api.registerAsIB(registerData);
      toast.success('Successfully registered as Introducing Broker!');
      setIsRegisterDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    }
  };

  const handlePayout = async (e) => {
    e.preventDefault();
    try {
      await api.requestPayout({ amount: payoutAmount, payment_method: 'bank_transfer' });
      toast.success('Payout request submitted!');
      setIsPayoutDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Payout request failed');
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${profile?.referral_code}`;
    navigator.clipboard.writeText(link);
    toast.success('Referral link copied!');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Not registered view
  if (!profile) {
    return (
      <div className="space-y-6" data-testid="ib-portal-page">
        <div className="text-center py-12">
          <Award className="w-16 h-16 mx-auto mb-4 text-primary" />
          <h1 className="text-2xl font-bold mb-2">Become an Introducing Broker</h1>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto">
            Earn commissions by referring traders to our platform. Get up to 1.5% commission per lot traded.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 max-w-3xl mx-auto mb-8">
            {Object.entries(tierColors).map(([tier, color]) => (
              <Card key={tier} className="text-center">
                <CardContent className="pt-6">
                  <Badge className={`${color} mb-2 capitalize`}>{tier}</Badge>
                  <p className="text-2xl font-bold">
                    {tier === 'bronze' ? '0.5%' : tier === 'silver' ? '0.75%' : tier === 'gold' ? '1.0%' : '1.5%'}
                  </p>
                  <p className="text-xs text-muted-foreground">per lot</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <Dialog open={isRegisterDialogOpen} onOpenChange={setIsRegisterDialogOpen}>
            <DialogTrigger asChild>
              <Button size="lg" className="glow-primary" data-testid="register-ib-btn">
                <Users className="w-4 h-4 mr-2" />
                Register as IB
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>IB Registration</DialogTitle>
                <DialogDescription>Fill in your details to become an Introducing Broker</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleRegister} className="space-y-4">
                <div className="space-y-2">
                  <Label>Company Name (Optional)</Label>
                  <Input
                    value={registerData.company_name}
                    onChange={(e) => setRegisterData({ ...registerData, company_name: e.target.value })}
                    placeholder="Your company name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Website (Optional)</Label>
                  <Input
                    value={registerData.website}
                    onChange={(e) => setRegisterData({ ...registerData, website: e.target.value })}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Marketing Methods</Label>
                  <Input
                    value={registerData.marketing_methods}
                    onChange={(e) => setRegisterData({ ...registerData, marketing_methods: e.target.value })}
                    placeholder="Social media, email, website..."
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsRegisterDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="submit-ib-registration">Register</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    );
  }

  // Registered IB view
  const tierProgress = dashboard?.next_tier?.referrals_needed 
    ? ((profile.total_referrals / (profile.total_referrals + dashboard.next_tier.referrals_needed)) * 100)
    : 100;

  return (
    <div className="space-y-6" data-testid="ib-portal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">IB Portal</h1>
          <p className="text-muted-foreground">Manage your referral network and commissions</p>
        </div>
        <div className="flex items-center gap-3">
          <Badge className={`${tierColors[profile.tier]} text-sm px-3 py-1`}>
            {profile.tier.toUpperCase()} TIER
          </Badge>
          <Button variant="outline" onClick={copyReferralLink} data-testid="copy-referral-btn">
            <Copy className="w-4 h-4 mr-2" />
            Copy Link
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="animate-fade-in-up">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Referrals</p>
                <p className="text-2xl font-bold font-mono">{profile.total_referrals}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-100">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <DollarSign className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Total Commission</p>
                <p className="text-2xl font-bold font-mono text-emerald-400">{formatCurrency(profile.total_commission)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-500/10">
                <Wallet className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Pending Payout</p>
                <p className="text-2xl font-bold font-mono text-amber-400">{formatCurrency(profile.pending_commission)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="animate-fade-in-up animation-delay-300">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/10">
                <TrendingUp className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Commission Rate</p>
                <p className="text-2xl font-bold font-mono">{profile.commission_rate}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Referral Code & Tier Progress */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Referral Code</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex-1 p-4 rounded-lg bg-muted font-mono text-xl text-center">
                {profile.referral_code}
              </div>
              <Button onClick={copyReferralLink}>
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Share this code or link with potential traders
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Tier Progress</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {dashboard?.next_tier?.name ? (
              <>
                <div className="flex items-center justify-between text-sm">
                  <Badge className={tierColors[profile.tier]}>{profile.tier}</Badge>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
                  <Badge className={tierColors[dashboard.next_tier.name]}>{dashboard.next_tier.name}</Badge>
                </div>
                <Progress value={tierProgress} className="h-2" />
                <p className="text-sm text-muted-foreground text-center">
                  {dashboard.next_tier.referrals_needed} more referrals to reach {dashboard.next_tier.name} tier
                </p>
              </>
            ) : (
              <div className="text-center py-4">
                <Award className="w-12 h-12 mx-auto mb-2 text-cyan-400" />
                <p className="text-lg font-medium">Maximum Tier Reached!</p>
                <p className="text-sm text-muted-foreground">You're at the highest commission rate</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Payout Button */}
      {profile.pending_commission >= 50 && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="font-medium">Ready for Payout</p>
              <p className="text-sm text-muted-foreground">
                You have {formatCurrency(profile.pending_commission)} available for withdrawal
              </p>
            </div>
            <Dialog open={isPayoutDialogOpen} onOpenChange={setIsPayoutDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="request-payout-btn">
                  <Wallet className="w-4 h-4 mr-2" />
                  Request Payout
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Request Commission Payout</DialogTitle>
                  <DialogDescription>Enter the amount you want to withdraw</DialogDescription>
                </DialogHeader>
                <form onSubmit={handlePayout} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Amount ($)</Label>
                    <Input
                      type="number"
                      value={payoutAmount}
                      onChange={(e) => setPayoutAmount(parseFloat(e.target.value))}
                      min={50}
                      max={profile.pending_commission}
                    />
                    <p className="text-xs text-muted-foreground">
                      Available: {formatCurrency(profile.pending_commission)} | Minimum: $50
                    </p>
                  </div>
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => setIsPayoutDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Submit Request</Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </CardContent>
        </Card>
      )}

      {/* Referrals Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Recent Referrals</CardTitle>
        </CardHeader>
        <CardContent>
          {referrals.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No referrals yet</p>
              <p className="text-sm">Share your referral link to start earning</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Trades</TableHead>
                  <TableHead>Volume</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Date</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referrals.map((referral) => (
                  <TableRow key={referral.id}>
                    <TableCell>{referral.referred_email}</TableCell>
                    <TableCell>
                      <Badge variant={referral.status === 'active' ? 'default' : 'secondary'}>
                        {referral.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-mono">{referral.trades_count}</TableCell>
                    <TableCell className="font-mono">{referral.volume_traded.toFixed(2)} lots</TableCell>
                    <TableCell className="font-mono text-emerald-400">
                      {formatCurrency(referral.commission_earned)}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(referral.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default IBPortal;
